import { useState, useEffect, useContext, createContext, useRef } from "react";
import wsw from "ws-wrapper";
import { Device } from "mediasoup-client";
import wsconnect from "../../ws/wsconnect";
import { constraints } from "../../ws/constraints";
import Transport from "../../ws/transport";
import Consumer from "../../ws/consumer";
import Producer from "../../ws/producer";
import { jwtDecode } from "jwt-decode";
import { INIT, CONNECTING, CONNECTED } from "../../constants/connectionStatus";
import { setupAudioRecording, sendRecordingToApi } from "./audioRecording";
import axios from "axios";
import { trackWebSocketEvent, logErrorToCrashlytics } from "../../firebase";

const WsContext = createContext();
const REQUEST_TIMEOUT = 10000;
const CONNECTION_TIMEOUT = 5000;

const RECONNECT_VALUE_MIN = 1000; // 1 second
const RECONNECT_VALUE_MAX = 1000 * 60; // 1 minute
const RECONNECT_VALUE_FACTOR = 1.4;
let timerInterval = null;

const WsWrapper = ({ children }) => {
  const device = new Device({});
  const ws = useRef(null);
  const sendTransport = useRef(null);
  const recvTransport = useRef(null);
  const streams = useRef(null);
  const userMe = useRef(null);
  const producer = useRef(null);
  const mediaRecorder = useRef(null);
  const recordedChunks = useRef([]);
  const mediaMedia = useRef(null);

  const [sendingRecording, setSendingRecording] = useState(null);
  const [mediaStream, setMediasetStream] = useState(null);
  const [token, setToken] = useState(null);
  const [dialing, setDialing] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const users = useRef([]);
  const [status, setStatus] = useState(INIT);
  const callIdRef = useRef(null);
  const [reconnectValue, setReconnectValue] = useState(RECONNECT_VALUE_MIN);
  const [timer, setTimer] = useState(0);
  const [micDisabled, setMicDisabled] = useState(false);
  const [audioDevices, setAudioDevices] = useState([]);
  const [currentAudioDeviceId, setCurrentAudioDeviceId] = useState(null);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [currentAudioOutputDeviceId, setCurrentAudioOutputDeviceId] =
    useState(null);
  const [sinkIdAvailable, setSinkIdAvailable] = useState(false);

  useEffect(() => {
    const el = document.getElementById("client-audio");
    if (el && el.setSinkId) {
      setSinkIdAvailable(true);
    }
  }, []);

  useEffect(() => {
    if (!activeCall) {
      setTimer(0);
    }
  }, [activeCall]);

  // useEffect(() => {
  // if (mediaStream) {
  //   connect(mediaStream);
  // }
  // return () => {
  //   if (timerInterval) {
  //     clearInterval(timerInterval);
  //   }
  // };
  // }, [mediaStream]);

  const connect = async (mediaStream, _token) => {
    let localToken = _token ? _token : token;
    const url = `wss://eu-de1.live.teech.tv:8443/?token=${localToken}&mic=true&`;

    // Track WebSocket connection attempt
    trackWebSocketEvent(
      "connect_attempt",
      {
        url,
        hasMediaStream: !!mediaStream,
      },
      null, // No user context available in WsWrapper
    );

    try {
      const wsConnection = await wsconnect(url, CONNECTION_TIMEOUT);
      ws.current = new wsw(wsConnection, { requestTimeout: REQUEST_TIMEOUT });
      ws.current.autoReconnect = true;
      window.wsw = ws.current;
      setStatus(CONNECTING);
    } catch (error) {
      console.error("WebSocket connection failed:", error);

      logErrorToCrashlytics(
        error,
        {
          context: "WsWrapper WebSocket connection error",
          url: url,
          hasMediaStream: !!mediaStream,
        },
        null,
      );
      setStatus(INIT);
      throw error; // Re-throw to allow caller to handle
    }

    ws.current.on("init", async (payload) => {
      try {
        users.current = [];
        await device.load({
          routerRtpCapabilities: payload.routerRtpCapabilities,
        });
        const { _sendTransport, _recvTransport } = await init({
          payload,
          device,
          sendRequest,
        });
        users.current = payload.clients;

        recvTransport.current = _recvTransport;
        sendTransport.current = _sendTransport;

        let consumer = null;

        for (const _stream of payload.streams) {
          consumer = new Consumer(sendRequest, device, recvTransport.current);
          const cProducer = _stream.producers.find((p) => p.kind === "audio");

          if (cProducer) {
            const _streams = [
              {
                clientId: _stream.clientId,
                id: _stream.id,
                consumer,
                producer: cProducer,
              },
            ];

            await consumer.consume(_stream.id, cProducer);
            await sendRequest("stream/resumeConsumer", {
              streamId: _stream.id,
            });

            try {
              const audioEl = document.getElementById("client-audio");
              audioEl.pause();
              audioEl.srcObject = null;
              audioEl.srcObject = consumer._mediaStream;
              audioEl.play();
            } catch (error) {
              console.log(error);
            }

            streams.current = _streams;
          }
        }
        if (consumer && consumer._mediaStream && mediaStream) {
          setupAudioRecording(
            mediaStream,
            consumer._mediaStream,
            recordedChunks,
            mediaRecorder,
          );
        }

        if (mediaStream) {
          startProducing({ mediaStream });
        } else if (!mediaStream) {
          createSnapshot({ _token });
        }
      } catch (error) {
        console.error("WebSocket init handler error:", error);
        logErrorToCrashlytics(
          error,
          {
            context: "WebSocket init handler",
            hasMediaStream: !!mediaStream,
          },
          null,
        );
      }
    });

    ws.current.on("disconnect", (event) => {
      console.debug("ws disconnect", event.code, event, event.wasClean);
      endCall();
      setStatus(INIT);
    });

    ws.current.on("error", (error) => {
      console.error("WebSocket error in WsWrapper:", error);

      logErrorToCrashlytics(
        error,
        {
          context: "WsWrapper WebSocket error",
        },
        null,
      );
    });

    ws.current.on("stream/close", async (payload) => {});

    ws.current.on("client/close", (payload) => {
      console.log("client/close");
      const leftUser = users.current.find((user) => user.id === payload.id);
      if (leftUser?.userInfo?.role === "ROLE_TEACHER") {
        endCall();
        setStatus(INIT);
      }
    });

    addHandler("stream/newProducer", async (payload) => {
      if (payload.kind === "audio") {
        const consumer = new Consumer(
          sendRequest,
          device,
          recvTransport.current,
        );
        const cProducer = {
          id: payload.producerId,
          kind: "auddio",
          paused: payload.paused,
        };

        const _streams = [
          {
            clientId: payload.clientId,
            id: payload.streamId,
            consumer,
            producer: cProducer,
          },
        ];

        await consumer.consume(payload.streamId, cProducer);
        await sendRequest("stream/resumeConsumer", {
          streamId: payload.streamId,
        });

        try {
          const audioEl = document.getElementById("client-audio");
          audioEl.pause();
          audioEl.srcObject = null;
          audioEl.srcObject = consumer._mediaStream;
          audioEl.play();
        } catch (error) {
          console.log(error);
        }

        streams.current = _streams;

        if (mediaMedia.current) {
          setupAudioRecording(
            mediaMedia.current,
            consumer._mediaStream,
            recordedChunks,
            mediaRecorder,
          );
        }
      }
    });

    ws.current.on("room/accessGranted", (payload) => {
      console.log("ws access granted", mediaMedia.current);
      setDialing(false);
      startProducing({ mediaStream: mediaMedia.current });
      const dialingEl = document.getElementById("dialing");
      if (dialingEl) {
        dialingEl.pause();
      }
    });

    ws.current.on("client/new", (payload) => {
      const decoded = jwtDecode(token);
      if (payload.userInfo.user_id === decoded.user_id) {
        userMe.current = payload;
      }
      users.current = [...users.current, payload];
    });

    ws.current.on("room/accessDenied", () => {
      setDialing(false);
      const el = document.getElementById("dialing");
      if (el) {
        el.pause();
      }

      if (mediaStream) {
        mediaStream.getAudioTracks().map((track) => {
          track.stop();
          track.enabled = false;
        });
      }

      if (mediaMedia.current) {
        mediaMedia.current.getAudioTracks().map((track) => {
          track.stop();
          track.enabled = false;
        });
      }
      setActiveCall(null);
      callIdRef.current = null;
    });
  };

  const addHandler = (event, fn) => {
    if (ws.current) {
      ws.current.on(event, async (...args) => {
        try {
          await fn(...args);
        } catch (error) {
          console.error("addHandler error", error);
          throw error;
        }
      });
    }
  };

  const createBlob = async () => {
    return await fetch(
      document.getElementById("canvas").toDataURL("image/jpeg", 0.8),
    )
      .then((res) => res.blob())
      .then((blob) => {
        return blob;
      });
  };

  const createSnapshot = async ({ _token }) => {
    try {
      setDialing(true);
      const file = await createBlob();
      const formdata = new FormData();
      formdata.append("snapshot", file);

      await axios.post(
        `https://eu-de1.live.teech.tv:8443/participant/snapshot/${_token}`,
        formdata,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const dialingEl = document.getElementById("dialing");
      if (dialingEl) {
        dialingEl.play();
      }
    } catch (e) {
      console.log(e);
    }
  };

  const sendRequest = async (request, ...args) => {
    if (ws.current) {
      try {
        return await ws.current.request(request, ...args);
      } catch (error) {
        console.error("sendRequest error", error);

        throw error;
      }
    }
  };

  const startProducing = async ({ mediaStream }) => {
    setTimer(0);
    if (timerInterval) {
      clearInterval(timerInterval);
    }
    const streamInfo = await sendRequest("stream/create", { kind: "mic" });
    const _streamId = streamInfo.streamId;

    const _producer = new Producer(
      _streamId,
      sendRequest,
      sendTransport.current,
    );
    producer.current = _producer;
    await _producer.produce(mediaStream);
    setStatus(CONNECTED);

    timerInterval = setInterval(() => {
      setTimer((prev) => prev + 1);
    }, 1000);
  };

  const endCall = async () => {
    setDialing(false);
    // Stop recording if mediaRecorder exists and is recording
    setMicDisabled(false);

    // Use the callId from ref (set when call started) or from activeCall
    const callId = callIdRef.current || (activeCall ? activeCall.callId : null);

    if (mediaRecorder.current && mediaRecorder.current.state !== "inactive") {
      mediaRecorder.current.stop();

      // Wait for the last dataavailable event before sending recording to API
      mediaRecorder.current.onstop = () => {
        if (recordedChunks.current.length > 0 && callId) {
          // Send recording to API using the captured callId
          console.log("Sending call recording to API for call:", callId);
          setSendingRecording(true);
          sendRecordingToApi(recordedChunks, callId)
            .then((result) => {
              setSendingRecording(false);
              if (result.success) {
                console.log("Recording successfully sent to API:", result);
              } else {
                console.log("Failed to send recording to API:", result.error);
              }
            })
            .catch((error) => {
              console.error("Error sending recording to API:", error);
              setSendingRecording(false);
            });
        } else {
          console.log(
            "Cannot send recording to API: " +
              (!recordedChunks.current.length
                ? "No recorded data available. "
                : "") +
              (!callId ? "No call ID available. " : ""),
          );
        }
      };
    }

    if (mediaStream) {
      mediaStream.getAudioTracks().map((track) => {
        track.stop();
        track.enabled = false;
      });
    }
    if (mediaMedia.current) {
      mediaMedia.current.getAudioTracks().map((track) => {
        track.stop();
        track.enabled = false;
      });
    }
    if (ws.current) {
      ws.current.disconnect();
      ws.current = null;
    }

    const audioEl = document.getElementById("client-audio");
    audioEl.pause();
    audioEl.srcObject = null;

    const dialingEl = document.getElementById("dialing");
    if (dialingEl) {
      dialingEl.pause();
    }

    const endCallEl = document.getElementById("end-call");
    if (endCallEl) {
      endCallEl.play();

      setTimeout(() => {
        endCallEl.pause();
      }, 2000);
    }

    setActiveCall(null);
    callIdRef.current = null;

    sendTransport.current = null;
    recvTransport.current = null;
    streams.current = null;
    userMe.current = null;
    producer.current = null;
    mediaRecorder.current = null;
    recordedChunks.current = [];

    setMediasetStream(null);
    mediaMedia.current = null;
    setToken(null);
    setDialing(false);
    users.current = [];
    setReconnectValue(RECONNECT_VALUE_MIN);
  };

  const hangup = async () => {
    const el = document.getElementById("dialing");
    if (el) {
      el.pause();
    }
    console.log("handup");
    if (mediaStream) {
      mediaStream.getAudioTracks().map((track) => {
        track.stop();
        track.enabled = false;
      });
    }
    if (mediaMedia.current) {
      mediaMedia.current.getAudioTracks().map((track) => {
        track.stop();
        track.enabled = false;
      });
    }
    if (ws.current) {
      ws.current.disconnect();
      ws.current = null;
    }

    const audioEl = document.getElementById("client-audio");
    audioEl.pause();
    audioEl.srcObject = null;
  };

  const startCall = async (_token, acceptCall = false) => {
    if (_token) {
      setToken(_token);
    }
    const _constraints = constraints({});

    try {
      const _mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: _constraints.audio,
        video: false,
      });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const _audioDevices = devices.filter(
        (device) => device.kind === "audioinput",
      );
      const _audioOutputDevices = devices.filter(
        (device) => device.kind === "audiooutput",
      );
      setAudioDevices(_audioDevices);
      setAudioOutputDevices(_audioOutputDevices);

      const audioTrack = _mediaStream.getAudioTracks()[0];
      if (audioTrack) {
        const settings = audioTrack.getSettings();
        setCurrentAudioDeviceId(settings.deviceId);
      }

      mediaMedia.current = _mediaStream;

      // Track successful media access
      trackWebSocketEvent(
        "media_access_success",
        {
          hasAudioTrack: !!audioTrack,
          audioDevicesCount: _audioDevices.length,
          audioOutputDevicesCount: _audioOutputDevices.length,
        },
        null, // No user context available in WsWrapper
      );

      if (acceptCall) {
        connect(_mediaStream, _token);
      } else {
        connect(null, _token);
      }
    } catch (error) {
      // Track media device errors
      logErrorToCrashlytics(
        error,
        {
          context: "startCall media access error",
          errorName: error.name,
          errorMessage: error.message,
          constraints: _constraints,
        },
        null, // No user context available in WsWrapper
      );
    }
  };

  const toggleMic = async () => {
    try {
      const response = await ws.current.request(
        micDisabled === false
          ? "stream/pauseProducer"
          : "stream/resumeProducer",
        {
          producerId: producer.current._audioProducer.id,
          streamId: producer.current._id,
        },
      );
      console.log(response);
      setMicDisabled(response.paused);
      return response;
    } catch (error) {
      return {
        ok: false,
      };
    }
  };

  const changeMic = async (deviceId) => {
    const _constraints = constraints({
      micDeviceId: { exact: deviceId },
    });

    mediaStream.current = await navigator.mediaDevices.getUserMedia({
      audio: _constraints.audio,
      video: false,
    });

    if (producer.current && producer.current._audioProducer) {
      try {
        // Replace the track directly in the producer
        const audioTrack = mediaStream.current.getAudioTracks()[0];
        producer.current._audioProducer.pause();
        await producer.current._audioProducer.replaceTrack({
          track: audioTrack,
        });
        producer.current._audioProducer.resume();
        console.log("Producer track replaced successfully");
      } catch (replaceError) {
        console.error("Error replacing track:", replaceError);

        // Fallback to toggle method
        if (toggleMic && !micDisabled) {
          await toggleMic(true);
          await toggleMic(false);
        }
      }
    }

    setCurrentAudioDeviceId(deviceId);
  };

  const changeAudioOutput = async (deviceId) => {
    try {
      const audioElement = document.getElementById("client-audio");
      if (audioElement && audioElement.setSinkId) {
        await audioElement.setSinkId(deviceId);
        setCurrentAudioOutputDeviceId(deviceId);
      } else {
        console.warn("setSinkId not supported by this browser");
      }
    } catch (error) {
      console.error("Failed to change audio output device:", error);
    }
  };

  return (
    <WsContext.Provider
      value={{
        ws: ws.current,
        dialing,
        setDialing,
        hangup,
        activeCall,
        setActiveCall: (call) => {
          if (call && call.callId) {
            callIdRef.current = call.callId;
          }
          setActiveCall(call);
        },
        token,
        startCall,
        setToken,
        streams: streams.current,
        toggleMic,
        status,
        setStatus,
        endCall,
        timer,
        micDisabled,
        audioDevices,
        currentAudioDeviceId,
        changeMic,
        audioOutputDevices,
        currentAudioOutputDeviceId,
        changeAudioOutput,
        sinkIdAvailable,
      }}
    >
      {sendingRecording && (
        <div className="loading">
          <span className="loader"></span>
          <div className="saving-recorded-call">
            Please wait... Saving recorded call
          </div>
        </div>
      )}
      {children}
    </WsContext.Provider>
  );
};

const init = async ({ payload, device, sendRequest }) => {
  console.debug("transport init");
  const response = { _sendTransport: null, _recvTransport: null };
  const { sendTransportInfo, recvTransportInfo } = payload;

  const sendTransport = new Transport({
    direction: "send",
    transportInfo: sendTransportInfo,
    device,
    sendRequest,
  });
  await sendTransport.create();
  response._sendTransport = sendTransport;

  const recvTransport = new Transport({
    direction: "recv",
    transportInfo: recvTransportInfo,
    device,
    sendRequest,
  });
  await recvTransport.create();
  console.debug("recv create");
  response._recvTransport = recvTransport._transport;

  return response;
};

export default WsWrapper;

export const useWs = () => useContext(WsContext);
